import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';

export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (isAuthenticated) return <Navigate to="/" />;

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-4">
      <div className="rounded-card border border-line bg-surface p-8 max-w-sm w-full">
        {/* Logo */}
        <div className="mb-7 flex justify-center">
          <img src="/logo-always-on-stock.png" alt="Always On Stock" className="h-28 w-auto" />
        </div>

        <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink mb-1">Anmelden</h2>
        <p className="text-[12.5px] text-ink-soft mb-6">Melden Sie sich bei Ihrem Konto an</p>

        {error && (
          <div className="mb-5 text-sm text-risk-red bg-risk-red-soft rounded-panel px-4 py-3">
            {error}
          </div>
        )}

        <form onSubmit={handlePasswordLogin} className="space-y-4">
          <div>
            <label className="block text-[12.5px] font-semibold text-ink-muted mb-1.5" htmlFor="login-email">
              E-Mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field pl-10"
                placeholder="ihre@email.de"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-[12.5px] font-semibold text-ink-muted mb-1.5" htmlFor="login-password">
              Passwort
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pl-10"
                placeholder="••••••••"
                required
              />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
            Anmelden
          </button>
        </form>
      </div>

      <footer className="mt-6 flex items-center gap-4 text-[12px]">
        <Link to="/impressum" className="font-medium text-ink-soft hover:text-ink transition-colors no-underline">Impressum</Link>
        <Link to="/datenschutz" className="font-medium text-ink-soft hover:text-ink transition-colors no-underline">Datenschutz</Link>
      </footer>
    </div>
  );
}
